import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  Search,
  Facebook,
  Music2,
  BarChart3,
  RefreshCw,
  Calendar,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers (mirrors the formatNumber in GoogleAds.js)
// ---------------------------------------------------------------------------
const formatNumber = (value, options = {}) => {
  if (value === undefined || value === null) return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';
  const { decimals = 1, isCost = false } = options;
  if (isCost) {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(num));
  }
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(decimals) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(decimals) + 'K';
  return num.toFixed(decimals);
};

const fmt = (v, isCost = false) =>
  v === null || v === undefined ? '—' : `₺${formatNumber(v, { isCost })}`;

const fmtOrders = (v) =>
  v === null || v === undefined ? '—' : formatNumber(v, { decimals: 0 });

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------
/**
 * Returns a Tailwind bg/text colour token based on pacing status.
 *
 * @param {'spend'|'revenue'|'orders'} metric
 * @param {number|null} pct          — the metric's completion percentage
 * @param {number}      expectedPct  — (days_passed / days_in_month) * 100
 */
const getStatus = (metric, pct, expectedPct) => {
  if (pct === null || pct === undefined) return 'gray';
  if (metric === 'spend') {
    // Spend is inverted: spending LESS than expected is good.
    if (pct <= expectedPct + 5) return 'green';
    if (pct <= expectedPct + 20) return 'amber';
    return 'red';
  }
  // Revenue & Orders: hitting target or above is good.
  if (pct >= expectedPct) return 'green';
  if (pct >= expectedPct - 20) return 'amber';
  return 'red';
};

const STATUS_PILL = {
  green: 'bg-green-100 text-green-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  gray: 'bg-gray-100 text-gray-500',
};

const STATUS_BAR = {
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  gray: '#d1d5db',
};

// ---------------------------------------------------------------------------
// Channel icon map
// ---------------------------------------------------------------------------
const CHANNEL_ICON = {
  GOOGLE_ADS: <Search className="w-4 h-4" />,
  META_ADS: <Facebook className="w-4 h-4" />,
  TIKTOK_ADS: <Music2 className="w-4 h-4" />,
};

const channelIcon = (key) =>
  CHANNEL_ICON[key] ?? <BarChart3 className="w-4 h-4" />;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const enhancedCardClass = 'rounded-3xl overflow-hidden border border-gray-200/80';

/** A single metric row inside a channel card */
const MetricRow = ({ label, mtd, target, pct, dailyRequired, isDailyOrders, expectedPct, metric }) => {
  const hasTarget = target !== null && target !== undefined;
  const status = hasTarget ? getStatus(metric, pct, expectedPct) : 'gray';
  const barPct = hasTarget && pct !== null ? Math.min(pct, 100) : 0;

  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center justify-between mb-1">
        <div className="flex-1 min-w-0 mr-3">
          <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">{label}</p>
          <p className="text-[11px] text-gray-600 tabular-nums mt-0.5">
            {metric === 'orders'
              ? `MTD: ${fmtOrders(mtd)} / target: ${fmtOrders(target)}`
              : `MTD: ${fmt(mtd, true)} / target: ${fmt(target, true)}`}
          </p>
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${STATUS_PILL[status]}`}>
          {pct !== null && pct !== undefined ? `${pct.toFixed(1)}%` : '—'}
        </span>
      </div>

      {hasTarget ? (
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1.5">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${barPct}%`, backgroundColor: STATUS_BAR[status] }}
          />
        </div>
      ) : (
        <p className="text-[10px] text-gray-400 italic mb-1.5">No target set</p>
      )}

      {hasTarget && (
        <p className="text-[12px] font-bold text-gray-800 tabular-nums">
          {isDailyOrders
            ? (dailyRequired !== null && dailyRequired !== undefined
                ? `Need ${formatNumber(dailyRequired, { decimals: 1 })} orders/day`
                : '—')
            : (dailyRequired !== null && dailyRequired !== undefined
                ? `Need ₺${formatNumber(dailyRequired, { isCost: true })}/day`
                : '—')}
        </p>
      )}
    </div>
  );
};

/** A single channel card */
const ChannelCard = ({ channel, expectedPct }) => {
  return (
    <div className={`${enhancedCardClass} bg-white p-4 flex flex-col`}>
      {/* Card header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="p-1.5 bg-blue-50 rounded-full border border-blue-100 text-blue-600 flex-shrink-0">
          {channelIcon(channel.channel_key)}
        </div>
        <h3 className="text-sm font-semibold text-gray-800">{channel.label}</h3>
      </div>

      <MetricRow
        label="Spend"
        metric="spend"
        mtd={channel.mtd_spend}
        target={channel.target_spend}
        pct={channel.spend_pct}
        dailyRequired={channel.daily_required_spend}
        isDailyOrders={false}
        expectedPct={expectedPct}
      />
      <MetricRow
        label="Revenue"
        metric="revenue"
        mtd={channel.mtd_revenue}
        target={channel.target_revenue}
        pct={channel.revenue_pct}
        dailyRequired={channel.daily_required_revenue}
        isDailyOrders={false}
        expectedPct={expectedPct}
      />
      <MetricRow
        label="Orders"
        metric="orders"
        mtd={channel.mtd_orders}
        target={channel.target_orders}
        pct={channel.orders_pct}
        dailyRequired={channel.daily_required_orders}
        isDailyOrders={true}
        expectedPct={expectedPct}
      />
    </div>
  );
};

/** Total summary card */
const TotalsCard = ({ totals }) => {
  const cols = [
    {
      key: 'spend',
      label: 'Total Spend',
      mtd: totals.mtd_spend,
      target: totals.target_spend,
      daily: totals.daily_required_spend,
      isOrders: false,
    },
    {
      key: 'revenue',
      label: 'Total Revenue',
      mtd: totals.mtd_revenue,
      target: totals.target_revenue,
      daily: totals.daily_required_revenue,
      isOrders: false,
    },
    {
      key: 'orders',
      label: 'Total Orders',
      mtd: totals.mtd_orders,
      target: totals.target_orders,
      daily: totals.daily_required_orders,
      isOrders: true,
    },
  ];

  return (
    <div className={`${enhancedCardClass} bg-white p-5 grid grid-cols-1 sm:grid-cols-3 gap-6`}>
      {cols.map(({ key, label, mtd, target, daily, isOrders }) => {
        const hasTarget = target !== null && target !== undefined;
        const pct =
          hasTarget && mtd !== null && target > 0
            ? Math.round((mtd / target) * 100)
            : null;

        return (
          <div key={key} className="flex flex-col gap-1">
            <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">{label}</p>
            <p className="text-2xl font-bold tabular-nums text-gray-900 mt-0.5">
              {isOrders ? fmtOrders(mtd) : fmt(mtd, true)}
            </p>
            {hasTarget && (
              <p className="text-[12px] text-gray-500">
                of {isOrders ? fmtOrders(target) : fmt(target, true)} target
                {pct !== null && (
                  <span className="ml-1.5 text-[11px] font-semibold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">
                    {pct}%
                  </span>
                )}
              </p>
            )}
            <p className="text-[14px] font-bold text-blue-700 tabular-nums mt-1">
              {isOrders
                ? daily !== null && daily !== undefined
                  ? `Need ${formatNumber(daily, { decimals: 1 })} orders/day`
                  : '—'
                : daily !== null && daily !== undefined
                  ? `Need ₺${formatNumber(daily, { isCost: true })}/day`
                  : '—'}
            </p>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Month name helper
// ---------------------------------------------------------------------------
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Pacing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Allow ?year=&month= overrides for debugging
  const params = new URLSearchParams(window.location.search);
  const now = new Date();
  const yearParam = params.get('year') || now.getFullYear();
  const monthParam = params.get('month') || now.getMonth() + 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `https://api.glowytics.com/api/pacing/overview?year=${yearParam}&month=${monthParam}`,
        {
          credentials: 'include',
          headers: {
            'X-Brand-ID': localStorage.getItem('currentBrandId') || '',
          },
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message || 'Failed to load pacing data');
      toast.error('Could not load pacing data. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  }, [yearParam, monthParam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---- Loading state ----
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="loading-logo-container" style={{ minHeight: 'auto' }}>
          <img
            src="/logoicon.png"
            alt="Loading"
            className="loading-logo"
            style={{ width: 48, height: 48 }}
          />
        </div>
      </div>
    );
  }

  // ---- Error state ----
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 gap-4">
        <BarChart3 className="w-12 h-12 text-gray-300" />
        <p className="text-gray-600 text-sm">{error}</p>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  // ---- Empty state ----
  if (!data || !data.channels || data.channels.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 gap-3 px-4 text-center">
        <BarChart3 className="w-12 h-12 text-gray-300" />
        <p className="text-gray-600 text-sm max-w-sm">
          No spending platforms or monthly targets configured for this month yet.
        </p>
      </div>
    );
  }

  const {
    year,
    month,
    days_in_month,
    days_passed,
    days_remaining,
    channels,
    totals,
  } = data;

  const expectedPct = days_in_month > 0 ? (days_passed / days_in_month) * 100 : 0;
  const monthName = MONTH_NAMES[(month ?? 1) - 1] ?? '';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* ---- Header ---- */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-bold text-gray-900">Budget Pacing</h1>
            <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
              Internal
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>
              {monthName} {year} &middot; Day {days_passed} of {days_in_month} &middot;{' '}
              {days_remaining} {days_remaining === 1 ? 'day' : 'days'} remaining
            </span>
            <button
              onClick={fetchData}
              className="ml-2 p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* ---- Totals card ---- */}
        {totals && (
          <div className="mb-6">
            <TotalsCard totals={totals} />
          </div>
        )}

        {/* ---- Per-channel grid ---- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {channels.map((ch) => (
            <ChannelCard key={ch.channel_key} channel={ch} expectedPct={expectedPct} />
          ))}
        </div>
      </div>
    </div>
  );
}
